import { Alert, CircularProgress } from '@mui/material'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PrimaryButton from '../buttons/PrimaryButton'
import StandardSelect, { SelectOption } from '../inputs/StandardSelect'
import useFetch from '../hooks/useFetch'
import { useTranslation } from 'react-i18next'
import UserContext from '../contexts/UserContext'
import { allowedLangOptions } from '../config/header'
import { ProcessLog, ProcessLogStatus } from '../models/ProcessLog'
import UploadIcon from '@mui/icons-material/Upload';
import { getProcessLogByNameRoute, postStartMenuImport, postStartUrlMenuImport } from '../config/apiRoutes'
import { differenceInDays, differenceInMinutes } from 'date-fns'
import { primary } from '../styles/colors'
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HelperText from '../inputs/HelperText'
import SnackbarContext from '../contexts/SnackbarContext'
import StandardTextInput from '../inputs/StandardTextInput'
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '../buttons/IconButton'

type Props = {
    onStatusChange?: () => void;
}

const selectOptions = [
    "replace",
    "deactivate",
    "add"
];

const allowedFileTypes = [
    "docx",
    "pdf",
    "txt",
    "html"
];

const maxFileSize = 10 * 1024 * 1024; //10MB

const MenuImport = (props: Props) => {
    const { t, i18n } = useTranslation();
    const snackbar = useContext(SnackbarContext);
    const { user, hasFeature, selectedRestaurant, } = useContext(UserContext);
    const importTypeOptions: SelectOption[] = useMemo(() => {
        let usedOptions: string[];
        if (!hasFeature("ADVANCED_MENU_IMPORT")) {
            usedOptions = ["replace"];
        } else {
            usedOptions = selectOptions;
        }
        return usedOptions.map(option => {
            return { label: t(`menu.import_options.${option}`), value: option }
        });
    }, [user.roles, t("menu.import_options")]);
    const [selectedImportType, setSelectedImportType] = useState<SelectOption | null>(importTypeOptions[0]);
    const langOptions: SelectOption[] = useMemo(() => {
        return allowedLangOptions.map(lang => { return { label: lang.label, value: lang.value } });
    }, []);
    const [selectedLang, setSelectedLang] = useState<SelectOption | null>(langOptions.find(l => l.value === i18n.language) || null);
    const processCaller = useFetch();
    const [lastImport, setLastImport] = useState<ProcessLog | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const uploader = useFetch(false, true);
    const urlUploader = useFetch();
    const lastImportStatus = useRef<ProcessLogStatus | null>(null);
    const [url, setUrl] = useState("");
    const showError = useMemo(() => {
        if (lastImport == null) return false;
        if (lastImport.status === ProcessLogStatus.ERROR) {
            return true;
        }
        if (lastImport.status === ProcessLogStatus.IN_PROGRESS) {
            const diffInMinutes = differenceInMinutes(new Date(), lastImport.createdAt);
            if (diffInMinutes > 16) { //in case there was an error and the process is stuck
                return true;
            }
        }
        return false;
    }, [lastImport]);
    const showStartImport = useMemo(() => {
        if (lastImport == null) return true;
        if (lastImport.status === ProcessLogStatus.SUCCESS || lastImport.status === ProcessLogStatus.ERROR) {
            return true;
        }
        if (lastImport.status === ProcessLogStatus.IN_PROGRESS) {
            const diffInMinutes = differenceInMinutes(new Date(), lastImport.createdAt);
            if (diffInMinutes > 16) { //in case there was an error and the process is stuck
                return true;
            }
        }
        return false;
    }, [lastImport]);

    const showSuccessMessage = useMemo(() => {
        if (lastImport == null) return false;
        if (lastImport.status === ProcessLogStatus.SUCCESS) {
            const diffInMinutes = differenceInDays(new Date(), lastImport.createdAt);
            if (diffInMinutes < 1) {
                return true;
            }
        };
        return false;
    }, [lastImport]);

    const showFileSizeError = useMemo(() => {
        if (selectedFile == null) return false;
        if (selectedFile.size > maxFileSize) return true;
        return false;
    }, [selectedFile]);

    const showFileTypeError = useMemo(() => {
        if (selectedFile == null) return false;
        const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();
        if (fileExtension == null) return true;
        if (!allowedFileTypes.includes(fileExtension)) return true;
        return false;
    }, [selectedFile]);

    const noFileSelectedError = useMemo(() => {
        return selectedFile == null && url === "";
    }, [selectedFile, url]);

    const bothSelectionError = useMemo(() => {
        return selectedFile != null && url !== "";
    }, [selectedFile, url]);

    const isUploadDisabled = useMemo(() => {
        if (noFileSelectedError || bothSelectionError) return true;
        return showFileSizeError || showFileTypeError;
    }, [showFileSizeError, showFileTypeError, selectedFile, noFileSelectedError, bothSelectionError]);

    const importTypeHelperText = useMemo(() => {
        if (selectedImportType == null) return "";
        return t(`menu.import_option_helpertexts.${selectedImportType?.value}`);
    }, [selectedImportType, t("menu.import_option_helpertexts")]);

    useEffect(() => {
        if (uploader.error && uploader.errorMessage) {
            snackbar.showMessage(uploader.errorMessage, "error");
        }
    }, [uploader.error, uploader.errorMessage])

    async function getLastImportStatus() {
        const url = getProcessLogByNameRoute
            .replace(":restaurantShortName", selectedRestaurant?.restaurantShortName || "")
            .replace(":processName", "MENU-IMPORT");
        const response = await processCaller.call<any, ProcessLog>("GET", url);
        if (response.ok && response.data) {
            setLastImport(response.data);
        }
    }

    useEffect(() => {
        getLastImportStatus();
        const interval = setInterval(() => {
            getLastImportStatus();
        }, 15 * 1000); //every 15 seconds

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (lastImport != null
            && lastImportStatus.current != null
            && props.onStatusChange != null
            && lastImport.status !== lastImportStatus.current
        ) {
            props.onStatusChange();
        }
        lastImportStatus.current = lastImport != null ? lastImport.status : null;
    }, [lastImport])

    async function handleImageSelected(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.files) return;
        if (e.target.files.length === 0) return;
        setSelectedFile(e.target.files[0]);
    }

    function handleSelectFileClick() {
        if (fileInputRef.current != null) {
            fileInputRef.current.click();
        }
    }

    function startImportClick() {
        if (selectedFile != null) {
            startImportUpload();
        }
        if (url !== "") {
            startImportUrl();
        }
    }

    async function startImportUpload() {
        if (selectedRestaurant == null) return;
        if (selectedFile == null) return;
        if (isUploadDisabled) return;
        const url = postStartMenuImport
            .replace(":restaurantShortName", selectedRestaurant?.restaurantShortName || "")
            .replace(":language", selectedLang?.value || "hu")
            .replace(":importType", selectedImportType?.value || "replace");

        const formData = new FormData();
        formData.append("file", selectedFile);
        const response = await uploader.call<any, any>("POST", url, formData);
        if (response.ok) {
            setSelectedFile(null);
            getLastImportStatus();
        }
    }

    async function startImportUrl() {
        if (selectedRestaurant == null) return;
        if (url == "") return;
        if (isUploadDisabled) return;
        const postUrl = postStartUrlMenuImport
            .replace(":restaurantShortName", selectedRestaurant?.restaurantShortName)
            .replace(":importType", selectedImportType?.value || "replace");
        const payload = {
            url: url,
            language: selectedLang?.value || "hu",
        }
        const response = await urlUploader.call<any, any>("POST", postUrl, payload);
        if (response.ok) {
            setUrl("");
            getLastImportStatus();
        }
    }

    return (
        <div>
            <div className="text-center font-bold text-[20px] md:mt-0 mt-3 text-primary-dark">
                {t("menu.menu_import")}
            </div>
            <input
                ref={fileInputRef}
                style={{ display: "none" }}
                type="file"
                accept=".doc,.docx,.pdf,.txt"
                onChange={handleImageSelected} />
            {showStartImport && (
                <>
                    {showSuccessMessage && (
                        <div className="mt-4">
                            <Alert severity='success'>
                                {t("menu.import_success")}
                            </Alert>
                        </div>
                    )}
                    {showError && lastImport != null && (
                        <div className="mt-4">
                            <Alert severity='error'>
                                {t("menu.import_failed")}
                            </Alert>
                        </div>
                    )}
                    <div className="grid grid-cols-12 gap-4 mt-6">
                        <div className="md:col-span-6 col-span-12">
                            <StandardSelect
                                options={importTypeOptions}
                                selectedOption={selectedImportType}
                                setSelectedOption={setSelectedImportType}
                                label={t("menu.import_type")}
                                helperText={importTypeHelperText}
                                disabled={!hasFeature("ADVANCED_MENU_IMPORT")}
                            />
                            {!hasFeature("ADVANCED_MENU_IMPORT") && (
                                <HelperText
                                >
                                    {t("menu.import_options_pro_helpertext")}
                                </HelperText>
                            )}
                        </div>
                        <div className="md:col-span-6 col-span-12">
                            <StandardSelect
                                options={langOptions}
                                selectedOption={selectedLang}
                                setSelectedOption={setSelectedLang}
                                label={t("common.language")}
                                helperText={t("menu.import_language_helpertext")}
                            />
                        </div>
                        <div className="md:col-span-6 col-span-12">
                            <StandardTextInput
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                label={t("menu.import_site_url")}
                                placeholder="https://example.com"
                            />
                        </div>
                        <div className="md:col-span-6 col-span-12">
                            <label className={`block font-bold text-[16px] text-primary-dark mb-2`}>
                                {t("menu.attached_document")}
                            </label>
                            <div className="pt-0.5 w-full ">
                                <div className="flex w-full gap-1">
                                    <div className="grow md:flex-none">
                                        <PrimaryButton
                                            fullWidthOnMobile
                                            onClick={handleSelectFileClick}
                                            startIcon={<AttachFileIcon />}
                                        >
                                            <>
                                                {selectedFile != null && (
                                                    <>
                                                        {selectedFile.name}
                                                    </>
                                                )}
                                                {selectedFile == null && (
                                                    <>
                                                        {t("common.no_file_selected")}
                                                    </>
                                                )}
                                            </>
                                        </PrimaryButton>
                                    </div>
                                    <IconButton
                                        variant="strike"
                                        onClick={() => setSelectedFile(null)}
                                        disabled={selectedFile == null}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </div>
                                {showFileSizeError && (<>
                                    <HelperText
                                        error
                                    >
                                        {t("common.max_file_size")}: {maxFileSize / (1024 * 1024)} MB.
                                    </HelperText>
                                </>)}
                                {showFileTypeError && (<>
                                    <HelperText
                                        error
                                    >
                                        {t("menu.allowed_file_types")}: {allowedFileTypes.join(", ")}
                                    </HelperText>
                                </>)}
                            </div>
                        </div>
                    </div>
                    {noFileSelectedError && (
                        <>
                            <HelperText
                                error
                            >
                                {t("menu.file_or_url_required")}
                            </HelperText>
                        </>
                    )}
                    {bothSelectionError && (
                        <>
                            <HelperText
                                error
                            >
                                {t("menu.file_and_url_error")}
                            </HelperText>
                        </>
                    )}
                    <div className="mt-4">
                        <Alert severity='info'>
                            <div>{t("menu.menu_import_tooltip")}</div>
                            <div>{t("menu.allowed_file_types")}: <strong>{allowedFileTypes.join(", ")}</strong></div>
                        </Alert>
                    </div>
                    {selectedImportType != null && selectedImportType.value === "replace" && (
                        <div className="">
                            <Alert severity='warning'>
                                {t("menu.menu_import_replace_warning")}
                            </Alert>
                        </div>
                    )}
                    <div className="mt-4 md:mb-0 mb-3">
                        <PrimaryButton
                            variant="strike"
                            startIcon={<UploadIcon />}
                            disabled={isUploadDisabled}
                            onClick={startImportClick}
                            fullWidthOnMobile
                        >
                            {t("menu.start_import")}
                        </PrimaryButton>
                    </div>
                </>
            )}
            {!showStartImport && (<>
                <div className="mt-3 text-center">
                    <div className="mb-5 mt-8">
                        <CircularProgress size="60px" style={{ color: primary.main }} />
                    </div>
                    <div className="text-primary text-[15px]">
                        {t("menu.import_in_progress")}
                    </div>
                    <div className="text-primary text-[15px] mb-6">
                        {t("menu.import_duration")}
                    </div>
                </div>
            </>)}
        </div>
    )
}

export default MenuImport